import { useMemo } from "react";

import { useAuth } from "@/_zustand/auth";
import {
  filtrarTarjetasPorComercio,
  esTarjetaValidaParaComercio,
} from "@/lib/oneclickCards";
import { useTransbankCredenciales } from "@/hooks/useTransbankCredenciales";

/**
 * useOneClickComercio()
 * Única capa que lee env + stores para resolver el "comercio efectivo" de
 * OneClick y derivar, EN CADA RENDER (nunca cacheado en un ref), la lista de
 * tarjetas válidas y la validez de `tempCard` para el comercio vigente. Así
 * el filtro es reactivo a cambios de máquina sin recargar (dos pestañas,
 * navegación entre carritos de distintas empresas).
 *
 * Delega la resolución de credenciales en useTransbankCredenciales("oneclick")
 * (generalización de resolverComercioEfectivo para incluir Webpay) — su API
 * pública (comercioEfectivo, tarjetasValidas, tempCardValida,
 * esTarjetaValida) no cambia por este refactor.
 *
 * Devuelve { comercioEfectivo, tarjetasValidas, tempCardValida, esTarjetaValida, credenciales }.
 */
export const useOneClickComercio = () => {
  const { cards, tempCard } = useAuth();

  const credenciales = useTransbankCredenciales("oneclick");
  const comercioEfectivo = credenciales.codeComerce;

  const tarjetasValidas = useMemo(
    () => filtrarTarjetasPorComercio(cards, comercioEfectivo),
    [cards, comercioEfectivo],
  );

  const tempCardValida = useMemo(
    () => esTarjetaValidaParaComercio(tempCard, comercioEfectivo),
    [tempCard, comercioEfectivo],
  );

  return {
    comercioEfectivo,
    tarjetasValidas,
    tempCardValida,
    esTarjetaValida: esTarjetaValidaParaComercio,
    credenciales,
  };
};
